import {useState} from "react";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import {TextField} from "@mui/material";
import {toast} from "sonner";
import {Box, Checkbox, Colors, CustomDialog, FormControlLabel, Text} from "@bounce/shared/ui";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {useTestSendTemplate} from "../../apis/requests/templates/testSendTemplate";
import {EmailDomainType, ReachTemplateContentChannel, ReachTemplateLanguage} from "../../apis/requests/templates/types";
import {MESSAGING_CHANNELS} from "./templateFormSchema";

export type TestSendLanguageContent = {
  subject: string | null;
  body: string;
  landingPageBody: string | null;
  preHeader: string | null;
  channelMessageCode: string | null;
};

type Props = {
  accountId: number;
  defaultRecipient: string;
  templateKey: string;
  contentChannel: ReachTemplateContentChannel;
  emailDomainType: EmailDomainType | null;
  contentByLanguage: Partial<Record<ReachTemplateLanguage, TestSendLanguageContent>>;
};

export const LAST_TEST_PHONE_KEY = "reach-test-send-last-phone";
export const LAST_TEST_EMAIL_KEY = "reach-test-send-last-email";

const LANGUAGE_OPTIONS: ReachTemplateLanguage[] = ["en", "es"];

const extractErrorMessage = (err: unknown): string => {
  if (err && typeof err === "object") {
    const e = err as {response?: {data?: {detail?: string}}; message?: string};
    return e.response?.data?.detail ?? e.message ?? "unknown error";
  }
  return "unknown error";
};

const TestSendDialog = NiceModal.create(
  ({accountId, defaultRecipient, templateKey, contentChannel, emailDomainType, contentByLanguage}: Props) => {
    const modal = useModal();
    const isMessaging = MESSAGING_CHANNELS.has(contentChannel);
    const availableLanguages = LANGUAGE_OPTIONS.filter((option) => !!contentByLanguage[option]);

    const [savedRecipient, setSavedRecipient] = useLocalStorageState<string>(
      isMessaging ? LAST_TEST_PHONE_KEY : LAST_TEST_EMAIL_KEY,
      "",
      {encrypted: false}
    );
    const [recipient, setRecipient] = useState(savedRecipient || defaultRecipient);
    const [addTestPrefix, setAddTestPrefix] = useState(true);
    const [selectedLanguages, setSelectedLanguages] = useState<Set<ReachTemplateLanguage>>(new Set(["en"]));
    const [isSending, setIsSending] = useState(false);

    const {mutateAsync} = useTestSendTemplate();

    const toggleLanguage = (language: ReachTemplateLanguage) => {
      setSelectedLanguages((prev) => {
        const next = new Set(prev);
        if (next.has(language)) {
          next.delete(language);
        } else {
          next.add(language);
        }
        return next;
      });
    };

    const handleSend = async () => {
      const languages = availableLanguages.filter((language) => selectedLanguages.has(language));
      if (!recipient.trim() || !languages.length) return;
      setIsSending(true);
      try {
        const variants = languages.map((language) => {
          const content = contentByLanguage[language]!;
          return {
            language,
            body: content.body,
            landing_page_body: content.landingPageBody,
            subject: content.subject,
            pre_header: content.preHeader,
            channel_message_code: content.channelMessageCode,
          };
        });
        const {results} = await mutateAsync({
          account_id: accountId,
          template_key: templateKey,
          content_channel: contentChannel,
          recipient: recipient.trim(),
          email_domain_type: emailDomainType,
          add_test_prefix: addTestPrefix,
          variants,
        });

        const succeeded = results.filter((result) => result.success);
        const failed = results.filter((result) => !result.success);

        if (succeeded.length) {
          setSavedRecipient(recipient.trim());
          const languageSuffix =
            succeeded.length > 1 ? ` (${succeeded.map((r) => r.language.toUpperCase()).join(", ")})` : "";
          toast.success(`Test ${isMessaging ? "SMS" : "email"} sent to ${recipient.trim()}${languageSuffix}`);
        }
        failed.forEach((result) => {
          const message = result.error ?? "Failed to send test";
          toast.error(results.length > 1 ? `${result.language.toUpperCase()}: ${message}` : message);
        });
        if (!failed.length) modal.hide();
      } catch (err) {
        toast.error(extractErrorMessage(err));
      } finally {
        setIsSending(false);
      }
    };

    const recipientLabel = isMessaging ? "Recipient phone *" : "Recipient email *";
    const canSend = !!recipient.trim() && selectedLanguages.size > 0 && !isSending;

    return (
      <CustomDialog
        title="Send test"
        forceTitle
        autoClose={false}
        buttonText={isSending ? "Sending…" : "Send test"}
        disabled={!canSend}
        onClick={handleSend}
      >
        <Text size={14}>
          Render this template against the previewed account and send it through the live{" "}
          {isMessaging ? "SMS" : "email"} pipeline to the recipient below.
        </Text>
        {availableLanguages.length > 1 && (
          <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <Text size={13} sx={{color: Colors.grey._700}}>
              Languages
            </Text>
            {availableLanguages.map((option) => (
              <FormControlLabel
                key={option}
                control={
                  <Checkbox checked={selectedLanguages.has(option)} onChange={() => toggleLanguage(option)} />
                }
                label={option.toUpperCase()}
              />
            ))}
          </Box>
        )}
        <TextField
          fullWidth
          size="small"
          label={recipientLabel}
          value={recipient}
          onChange={(e) => setRecipient(e.target.value)}
          placeholder={isMessaging ? "+15551234567" : "you@bounceai.com"}
        />
        {!isMessaging && (
          <FormControlLabel
            control={
              <Checkbox
                checked={addTestPrefix}
                onChange={(e) => setAddTestPrefix(e.target.checked)}
              />
            }
            label="Add [R-TEST] to subject"
          />
        )}
      </CustomDialog>
    );
  }
);

export default TestSendDialog;
